import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@capacit-com/lara-law-shared-ui";
import { Separator } from "@capacit-com/lara-law-shared-ui";
import { TagType } from "@/models/tag/tag";
import useTagsManagement from "../hooks/useTagManagement";
import { CreateTagPopover } from "../components/CreateTagPopover";
import { TagCard } from "../components/TagCard";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";

interface TagsManagementProps {
  search: string;
}

export default function TagsManagement({ search }: TagsManagementProps) {
  const {
    loading,
    handleCreateTag,
    handleUpdateTag,
    handleDeleteTag,
    searchTags,
    departmentTags,
    regularTags,
  } = useTagsManagement();

  const filteredTags = useMemo(() => {
    return searchTags(search);
  }, [search, searchTags]);

  const filteredDepartmentTags = useMemo(() => {
    return filteredTags.filter((tag) => tag.tagType === TagType.Department);
  }, [filteredTags]);

  const filteredRegularTags = useMemo(() => {
    return filteredTags.filter((tag) => tag.tagType === TagType.Tag);
  }, [filteredTags]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="h-32 animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-4 pt-2">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Afdelinger
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{departmentTags.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{regularTags.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Departments Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Afdelinger</h3>
          <PermissionGate allOf={[Permission.CreateTag]}>
            <CreateTagPopover
              onCreateTag={handleCreateTag}
              tagType={TagType.Department}
            />
          </PermissionGate>
        </div>

        {filteredDepartmentTags.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredDepartmentTags.map((tag) => (
              <TagCard
                key={tag.id}
                tag={tag}
                onUpdate={handleUpdateTag}
                onDelete={handleDeleteTag}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              {search
                ? "Ingen afdelinger matcher din søgning"
                : "Ingen afdelinger oprettet endnu"}
            </CardContent>
          </Card>
        )}
      </div>

      <Separator />

      {/* Regular Tags Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Tags</h3>
          <PermissionGate allOf={[Permission.CreateTag]}>
            <CreateTagPopover
              onCreateTag={handleCreateTag}
              tagType={TagType.Tag}
            />
          </PermissionGate>
        </div>

        {filteredRegularTags.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredRegularTags.map((tag) => (
              <TagCard
                key={tag.id}
                tag={tag}
                onUpdate={handleUpdateTag}
                onDelete={handleDeleteTag}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              {search
                ? "Ingen tags matcher din søgning"
                : "Ingen tags oprettet endnu"}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
